<script lang="ts">
  import Layout from "../../components/layout/Layout.svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import { addNotification } from "../../lib/stores";
  import type { Trip } from "../../lib/types";

  let { trip, user = null, recommendations = [], meta = {} }: {
    trip: Trip;
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
    recommendations?: Array<{
      id?: string;
      name?: string;
      place_name?: string;
      category: string;
      rating?: number;
      price_level?: number;
      description?: string;
      reason?: string;
      image_url?: string;
      address?: string;
      recommendation_score?: number;
    }>;
    meta?: { similar_users_count?: number; context?: Record<string, unknown> };
  } = $props();

  let loading = $state(false);
  let refreshing = $state(false);

  const CATEGORY_LABELS: Record<string, string> = {
    restaurant: "맛집",
    cafe: "카페",
    attraction: "관광지",
    shopping: "쇼핑",
    accommodation: "숙소",
    activity: "액티비티",
    culture: "문화",
    nature: "자연",
  };

  function getCategoryColor(category: string): string {
    const colors: Record<string, string> = {
      restaurant: "rgba(251,146,60,0.15)",
      cafe: "rgba(167,139,250,0.15)",
      attraction: "rgba(32,178,170,0.15)",
      shopping: "rgba(251,113,133,0.15)",
      accommodation: "rgba(74,222,128,0.15)",
      activity: "rgba(96,165,250,0.15)",
      culture: "rgba(251,191,36,0.15)",
      nature: "rgba(52,211,153,0.15)",
    };
    return colors[category] || "rgba(255,255,255,0.06)";
  }

  function getCategoryTextColor(category: string): string {
    const colors: Record<string, string> = {
      restaurant: "#fb923c",
      cafe: "#a78bfa",
      attraction: "#20b2aa",
      shopping: "#fb7185",
      accommodation: "#4ade80",
      activity: "#60a5fa",
      culture: "#fbbf24",
      nature: "#34d399",
    };
    return colors[category] || "rgba(255,255,255,0.5)";
  }

  function renderStars(rating: number): string {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return "★".repeat(full) + (half ? "☆" : "") + "☆".repeat(5 - full - (half ? 1 : 0));
  }

  async function refreshRecommendations() {
    if (refreshing) return;
    refreshing = true;
    try {
      const res = await fetch(`/api/v1/trips/${trip.id}/recommendations/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        recommendations = data.data || [];
        addNotification("success", "추천이 갱신되었습니다.");
      } else {
        addNotification("error", "갱신에 실패했습니다.");
      }
    } catch {
      addNotification("error", "오류가 발생했습니다.");
    } finally {
      refreshing = false;
    }
  }
</script>

<Layout {user}>
  <TripSubNav {trip} currentPage="recommendations" />

  <div class="max-w-4xl mx-auto px-4 py-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-xl font-bold" style="color: var(--text-primary);">AI 추천</h2>
        <p class="text-sm mt-0.5" style="color: var(--text-muted);">
          {trip.destination} 여행을 위한 맞춤 추천
          {#if meta.similar_users_count}
            <span class="ml-1" style="color: var(--accent);">• {meta.similar_users_count}명의 유사 여행자 분석</span>
          {/if}
        </p>
      </div>
      <button
        onclick={refreshRecommendations}
        disabled={refreshing}
        class="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50"
        style="background: var(--accent-dim); color: var(--accent);"
      >
        <svg class="w-4 h-4 {refreshing ? 'animate-spin' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        {refreshing ? "갱신 중..." : "새로고침"}
      </button>
    </div>

    {#if recommendations.length === 0}
      <!-- Empty state -->
      <div class="flex flex-col items-center justify-center py-24 text-center">
        <div class="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style="background: var(--accent-dim);">
          <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--accent);">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <h3 class="text-lg font-semibold mb-2" style="color: var(--text-primary);">아직 추천 장소가 없어요</h3>
        <p class="text-sm mb-6" style="color: var(--text-muted);">
          여행 일정과 지출 기록을 추가하면<br />AI가 맞춤 장소를 추천해드립니다
        </p>
        <button
          onclick={refreshRecommendations}
          disabled={refreshing}
          class="px-6 py-3 rounded-xl font-medium text-sm transition-colors disabled:opacity-50"
          style="background: var(--accent); color: #000;"
        >
          {refreshing ? "분석 중..." : "AI 추천 받기"}
        </button>
      </div>
    {:else}
      <!-- Recommendation Cards -->
      <div class="grid gap-4 sm:grid-cols-2">
        {#each recommendations as rec}
          <div
            class="rounded-2xl overflow-hidden transition-all"
            style="background: var(--surface); border: 1px solid var(--border);"
          >
            {#if rec.image_url}
              <div class="h-44 overflow-hidden">
                <img
                  src={rec.image_url}
                  alt={rec.name || rec.place_name}
                  class="w-full h-full object-cover"
                  onerror={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            {:else}
              <div class="h-32 flex items-center justify-center" style="background: rgba(32,178,170,0.06);">
                <svg class="w-12 h-12 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--accent);">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            {/if}

            <div class="p-4 space-y-3">
              <!-- Name & Category -->
              <div class="flex items-start gap-2">
                <div class="flex-1 min-w-0">
                  <h3 class="font-semibold text-base leading-tight" style="color: var(--text-primary);">
                    {rec.name || rec.place_name || "알 수 없는 장소"}
                  </h3>
                  {#if rec.address}
                    <p class="text-xs mt-0.5 truncate" style="color: var(--text-muted);">{rec.address}</p>
                  {/if}
                </div>
                <span class="flex-shrink-0 px-2 py-1 rounded-lg text-xs font-medium"
                  style="background: {getCategoryColor(rec.category)}; color: {getCategoryTextColor(rec.category)};">
                  {CATEGORY_LABELS[rec.category] || rec.category}
                </span>
              </div>

              <!-- Rating & Price -->
              {#if rec.rating || rec.price_level}
                <div class="flex items-center gap-3">
                  {#if rec.rating}
                    <span class="text-sm" style="color: #fbbf24;" title={`${rec.rating}/5`}>
                      {renderStars(rec.rating)}
                      <span class="ml-1 text-xs" style="color: var(--text-muted);">{rec.rating.toFixed(1)}</span>
                    </span>
                  {/if}
                  {#if rec.price_level}
                    <span class="text-sm font-medium" style="color: var(--accent);">
                      {"₩".repeat(rec.price_level)}
                    </span>
                  {/if}
                  {#if rec.recommendation_score}
                    <span class="ml-auto text-xs px-2 py-0.5 rounded-full"
                      style="background: rgba(32,178,170,0.12); color: var(--accent);">
                      매칭 {Math.round(rec.recommendation_score * 100)}%
                    </span>
                  {/if}
                </div>
              {/if}

              <!-- Description -->
              {#if rec.description}
                <p class="text-sm leading-relaxed line-clamp-2" style="color: var(--text-secondary);">
                  {rec.description}
                </p>
              {/if}

              <!-- Reason -->
              {#if rec.reason}
                <div class="flex gap-2 p-3 rounded-xl" style="background: rgba(32,178,170,0.08);">
                  <svg class="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--accent);">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <p class="text-xs leading-relaxed italic" style="color: var(--text-secondary);">{rec.reason}</p>
                </div>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</Layout>

<style>
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
